import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "../shops/SidebarLayout";
import "./CampaignDetailsPage.css";

const CampaignDetailsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, sent, failed, opened, clicked
  const [businessData, setBusinessData] = useState(null);

  useEffect(() => {
    fetchCampaignDetails();
    fetchBusinessData();
  }, [campaignId]);

  const fetchBusinessData = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/shops/business-profile`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBusinessData(response.data.business);
    } catch (err) {
    }
  };

  const fetchCampaignDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      // Fetch campaign details
      const campaignRes = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/marketing/campaigns/${campaignId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCampaign(campaignRes.data.campaign || campaignRes.data);

      // Fetch recipients list
      const recipientsRes = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/marketing/campaigns/${campaignId}/recipients`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRecipients(recipientsRes.data);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load campaign details",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const exportToCSV = () => {
    const headers = ["Name", "Email", "Status", "Opened", "Clicked", "Sent At"];
    const rows = filteredRecipients.map((r) => [
      r.name || "N/A",
      r.email,
      r.status,
      r.opened ? "Yes" : "No",
      r.clicked ? "Yes" : "No",
      formatDate(r.sentAt),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaign-${campaign?.name || "export"}-recipients.csv`;
    a.click();
  };

  // Replace merge fields with actual data
  const getPreviewContent = () => {
    if (!campaign.content) return "";

    let previewContent = campaign.content;

    // Use first recipient's data or placeholder
    const sampleRecipient = recipients[0];
    const firstName = sampleRecipient?.name?.split(" ")[0] || "Customer";
    const lastName = sampleRecipient?.name?.split(" ").slice(1).join(" ") || "";

    // Replace merge fields
    previewContent = previewContent.replace(/\{\{firstName\}\}/g, firstName);
    previewContent = previewContent.replace(/\{\{lastName\}\}/g, lastName);
    previewContent = previewContent.replace(
      /\{\{businessName\}\}/g,
      businessData?.name || "Your Business",
    );

    // Replace any remaining merge fields with [Field Name]
    previewContent = previewContent.replace(
      /\{\{(\w+)\}\}/g,
      (match, field) => {
        return `<span style="background: #fff3cd; padding: 2px 6px; border-radius: 3px;">[${field}]</span>`;
      },
    );

    return previewContent;
  };

  if (loading) {
    return (
      <div className="campaign-details-page marketing-page">
        <div className="loading-spinner">Loading campaign details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="campaign-details-page marketing-page">
        <div className="error-message">{error}</div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/dashboard/marketing")}
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  if (!campaign) return null;

  const analytics = campaign.analytics || {};
  const totalSent = analytics.totalSent || 0;
  const totalFailed = analytics.totalFailed || 0;
  const opens = analytics.opens || 0;
  const clicks = analytics.clicks || 0;
  const totalRecipients = totalSent + totalFailed;
  const openRate = totalSent > 0 ? ((opens / totalSent) * 100).toFixed(1) : 0;
  const clickRate = totalSent > 0 ? ((clicks / totalSent) * 100).toFixed(1) : 0;

  // Filter recipients
  const filteredRecipients = recipients.filter((recipient) => {
    const matchesSearch =
      recipient.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipient.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === "all" ||
      (filterStatus === "sent" && recipient.status === "sent") ||
      (filterStatus === "failed" && recipient.status === "failed") ||
      (filterStatus === "opened" && recipient.opened) ||
      (filterStatus === "clicked" && recipient.clicked);

    return matchesSearch && matchesFilter;
  });

  return (
    <SidebarLayout>
      <div className="campaign-details-page marketing-page">
        {/* Header */}
        <div className="page-header">
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/dashboard/marketing")}
          >
            ← Back to Campaigns
          </button>
          <h1>📊 Campaign Analytics</h1>
        </div>

        {/* Campaign Info */}
        <div className="detail-section">
          <h2>Campaign Information</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Campaign Name</label>
              <span>{campaign.name}</span>
            </div>
            <div className="detail-item">
              <label>Subject Line</label>
              <span>{campaign.subject}</span>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <span className={`status-badge status-${campaign.status}`}>
                {campaign.status}
              </span>
            </div>
            <div className="detail-item">
              <label>Audience Type</label>
              <span className="audience-badge">
                {campaign.audienceType?.replace(/_/g, " ") || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Delivery Stats */}
        <div className="detail-section">
          <h2>Delivery Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📧</div>
              <div className="stat-content">
                <div className="stat-value">{totalRecipients}</div>
                <div className="stat-label">Total Recipients</div>
              </div>
            </div>
            <div className="stat-card success">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <div className="stat-value">{totalSent}</div>
                <div className="stat-label">Successfully Sent</div>
              </div>
            </div>
            <div className="stat-card error">
              <div className="stat-icon">❌</div>
              <div className="stat-content">
                <div className="stat-value">{totalFailed}</div>
                <div className="stat-label">Failed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement Stats */}
        <div className="detail-section">
          <h2>Engagement Metrics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👁️</div>
              <div className="stat-content">
                <div className="stat-value">{opens}</div>
                <div className="stat-label">Opens</div>
                <div className="stat-percentage">{openRate}% open rate</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🖱️</div>
              <div className="stat-content">
                <div className="stat-value">{clicks}</div>
                <div className="stat-label">Clicks</div>
                <div className="stat-percentage">{clickRate}% click rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recipients List */}
        <div className="detail-section">
          <div className="recipients-header">
            <h2>Recipients ({filteredRecipients.length})</h2>
            <button className="btn btn-secondary" onClick={exportToCSV}>
              📥 Export CSV
            </button>
          </div>

          {/* Filters */}
          <div className="recipients-filters">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Recipients</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="opened">Opened</option>
              <option value="clicked">Clicked</option>
            </select>
          </div>

          {/* Recipients Table */}
          <div className="recipients-table-wrapper">
            <table className="recipients-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Opened</th>
                  <th>Clicked</th>
                  <th>Sent At</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecipients.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      No recipients found
                    </td>
                  </tr>
                ) : (
                  filteredRecipients.map((recipient, index) => (
                    <tr key={recipient._id || index}>
                      <td data-label="Name">{recipient.name || "N/A"}</td>
                      <td data-label="Email">{recipient.email}</td>
                      <td data-label="Status">
                        <span
                          className={`recipient-status status-${recipient.status}`}
                        >
                          {recipient.status}
                        </span>
                      </td>
                      <td data-label="Opened">
                        {recipient.opened ? "✅ Yes" : "—"}
                      </td>
                      <td data-label="Clicked">
                        {recipient.clicked ? "🖱️ Yes" : "—"}
                      </td>
                      <td data-label="Sent At">
                        {formatDate(recipient.sentAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Email Preview */}
        {campaign.content && (
          <div className="detail-section">
            <h2>Email Content Preview</h2>
            <p
              style={{
                fontSize: "13px",
                color: "#6c757d",
                marginBottom: "12px",
              }}
            >
              Showing actual content as sent to recipients (using first
              recipient's data as example)
            </p>
            <div className="email-preview-box">
              <div className="email-subject">
                <strong>Subject:</strong> {campaign.subject}
              </div>
              <div
                className="email-content-preview"
                dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
              />
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default CampaignDetailsPage;
